/**
 * Central route/slug map for the marketing website (DE on root, no prefix).
 * Single source of truth for all internal marketing links – see
 * docs/superpowers/specs/2026-06-03-marketing-relaunch-foundation.md §1.2.
 * EN/FR slugs come with the Phase-2 translation wave.
 */
export const marketingRoutes = {
  home: '/',
  service: '/so-funktionierts',
  funktionen: '/funktionen',
  warumLocari: '/warum-locari',
  antiProfiling: '/anti-profiling',
  preise: '/preise',
  faq: '/faq',
  ueberUns: '/ueber-uns',
  blog: '/blog',
  changelog: '/changelog',
  kontakt: '/kontakt',
  karriere: '/karriere',
  presse: '/presse',
  sicherheit: '/sicherheit',
  fuerVermieter: '/fuer-vermieter',
  fuerGrossvermieter: '/fuer-grossvermieter',
  ki: '/ki',
  mieterselbstauskunft: '/mieterselbstauskunft',
  hilfe: '/hilfe',
  verhaltenskodex: '/verhaltenskodex',
  vergleich: {
    everreal: '/vergleich-everreal',
    immocloud: '/vergleich-immocloud',
    objego: '/vergleich-objego',
    ohneMakler: '/vergleich-ohne-makler',
    vermietetDe: '/vergleich-vermietet-de',
  },
  legal: {
    hub: '/legal',
    impressum: '/legal/impressum',
    datenschutz: '/legal/datenschutz',
    cookieRichtlinie: '/legal/cookie-richtlinie',
    widerruf: '/legal/widerruf',
    kuendigung: '/legal/kuendigung',
    dsaMeldung: '/legal/dsa-meldung',
    subProzessoren: '/legal/sub-prozessoren',
    avv: '/legal/avv',
    agbB2b: '/legal/agb-b2b',
    agbB2c: '/legal/agb-b2c',
    nutzungsbedingungen: '/legal/nutzungsbedingungen',
    responsibleDisclosure: '/legal/responsible-disclosure',
    barrierefreiheit: '/legal/barrierefreiheit',
  },
  auth: { signIn: '/auth/sign-in', signUp: '/auth/sign-up' },
} as const;

/** Contact form deep-link with a preselected topic (see contact form / footer). */
export function kontaktTopic(topic: string) {
  return `${marketingRoutes.kontakt}?topic=${topic}`;
}

/**
 * Auth-Link mit Locale-Bridge.
 *
 * Marketing nutzt PFAD-basiertes i18n (`/` = de, `/en`, `/fr`), die App
 * (Sign-up/Sign-in/Home) dagegen COOKIE/HEADER-basiertes i18n
 * (`createI18nServerInstance`). Ohne Brücke rendert `/auth/sign-up` je nach
 * Browser-`Accept-Language` (oder altem `lang`-Cookie) in einer anderen Sprache
 * als die Marketing-Seite, von der der Nutzer gerade kam – z.B. englische
 * Sign-up-Seite trotz deutscher Marketing-Seite, weil der deutsche Marketing-
 * Content pfad-fest ist und die echte EN-Auflösung maskiert.
 *
 * `?lang=<locale>` wird von der Middleware (`handleLanguageQueryParam`) in den
 * `lang`-Cookie übersetzt → die App übernimmt exakt die im Marketing aktive
 * Sprache. Gleicher Mechanismus wie bei den Consent-/E-Mail-Links.
 */
export function authHref(
  kind: keyof typeof marketingRoutes.auth,
  locale: Locale,
): string {
  return `${marketingRoutes.auth[kind]}?lang=${locale}`;
}

/* ============================================================================
   EN/FR Phase 2 (Welle 1) – locale-aware Routing, ADDITIV zu marketingRoutes.
   Nur die 8 übersetzten Kern-Seiten. DE = Root ohne Präfix, EN/FR = Präfix +
   lokalisierter Slug. Spec: 2026-06-03-marketing-enfr-welle1.md.
   ========================================================================== */

export type Locale = 'de' | 'en' | 'fr';
export const LOCALES: readonly Locale[] = ['de', 'en', 'fr'] as const;

/** Lokalisierte Slugs der 8 übersetzten Kern-Seiten (einzige Quelle der Wahrheit). */
export const localizedSlugs = {
  home: { de: '/', en: '/en', fr: '/fr' },
  service: {
    de: '/so-funktionierts',
    en: '/en/how-it-works',
    fr: '/fr/comment-ca-marche',
  },
  warumLocari: {
    de: '/warum-locari',
    en: '/en/why-locari',
    fr: '/fr/pourquoi-locari',
  },
  ki: { de: '/ki', en: '/en/ai', fr: '/fr/ia' },
  preise: { de: '/preise', en: '/en/pricing', fr: '/fr/tarifs' },
  faq: { de: '/faq', en: '/en/faq', fr: '/fr/faq' },
  ueberUns: { de: '/ueber-uns', en: '/en/about', fr: '/fr/a-propos' },
  kontakt: { de: '/kontakt', en: '/en/contact', fr: '/fr/contact' },
  // Produkt-/Firmenseiten (zweite Welle, alle Nicht-Legal)
  whatsapp: { de: '/whatsapp', en: '/en/whatsapp', fr: '/fr/whatsapp' },
  sicherheit: { de: '/sicherheit', en: '/en/security', fr: '/fr/securite' },
  hilfe: { de: '/hilfe', en: '/en/help', fr: '/fr/aide' },
  changelog: { de: '/changelog', en: '/en/changelog', fr: '/fr/changelog' },
  karriere: { de: '/karriere', en: '/en/careers', fr: '/fr/carrieres' },
  presse: { de: '/presse', en: '/en/press', fr: '/fr/presse' },
  verhaltenskodex: {
    de: '/verhaltenskodex',
    en: '/en/code-of-conduct',
    fr: '/fr/code-de-conduite',
  },
  fuerVermieter: {
    de: '/fuer-vermieter',
    en: '/en/for-landlords',
    fr: '/fr/pour-bailleurs',
  },
  fuerGrossvermieter: {
    de: '/fuer-grossvermieter',
    en: '/en/for-portfolio-managers',
    fr: '/fr/pour-gestionnaires',
  },
  antiProfiling: {
    de: '/anti-profiling',
    en: '/en/anti-profiling',
    fr: '/fr/anti-profilage',
  },
  /**
   * Erste und bislang EINZIGE übersetzte Legal-Route. Alle anderen `/legal/*`
   * bleiben in `DE_ONLY_PATHS` — sie haben keine freigegebene Fassung in EN/FR.
   *
   * Grundlage: Legal-Freigabe vom 03.08.2026
   * (`docs/legal/freigabe-widerrufsbelehrung-en-fr-2026-08-03.md`). Der
   * `legal/`-Pfadteil bleibt in allen drei Sprachen gleich, nur das Dokument
   * trägt den lokalisierten Slug — dieselbe Logik wie beim Hilfe-Center.
   *
   * ⚠️ hreflang bleibt SPRACHbezogen (`en`, `fr`), nie länderbezogen
   * (`en-GB`, `fr-FR`). Das ist keine Stilfrage: Ein Länderbezug wäre ein Indiz
   * für ein „Ausrichten" i.S.v. Art. 6 Rom I auf FR/UK, das es nicht geben soll
   * (`docs/legal/widerrufsbelehrung-uebersetzung-en-fr-2026-08-03.md` §7.5).
   * Sitemap und `buildMarketingMetadata` erzeugen genau das — nicht ändern.
   */
  legalWiderruf: {
    de: '/legal/widerruf',
    en: '/en/legal/withdrawal',
    fr: '/fr/legal/retractation',
  },
  // Ad-Landing-Pages (noindex). DE auf Root, EN/FR mit Präfix. Aus der Sitemap
  // ausgeschlossen (NOINDEX_KEYS), aber in localizedSlugs für route()/
  // pathToTranslatedKey()/buildLpMetadata() + das #564-Sprach-Banner.
  concierge: {
    de: '/lp/concierge',
    en: '/en/lp/concierge',
    fr: '/fr/lp/concierge',
  },
  wg: { de: '/lp/wg', en: '/en/lp/wg', fr: '/fr/lp/wg' },
} as const;

export type TranslatedKey = keyof typeof localizedSlugs;
export const TRANSLATED_KEYS = Object.keys(localizedSlugs) as TranslatedKey[];

/**
 * Keys, deren EN/FR-Seiten BEREITS live sind. Steuert NUR den Footer-Sprachumschalter
 * (kein Link auf eine noch nicht existierende Seite → kein 404). Pro ausgelieferter
 * Seite hier ergänzen. Leer = Switcher überall disabled (Fundament-PR).
 */
export const LIVE_TRANSLATED_KEYS = new Set<TranslatedKey>([
  'home',
  'service',
  'warumLocari',
  'ki',
  'preise',
  'faq',
  'ueberUns',
  'kontakt',
  'sicherheit',
  'hilfe',
  'changelog',
  'karriere',
  'presse',
  'verhaltenskodex',
  'fuerVermieter',
  'fuerGrossvermieter',
  'antiProfiling',
  'whatsapp',
  'legalWiderruf',
  'concierge',
  'wg',
]);

/**
 * Keys, die NICHT in die Sitemap gehören (noindex Ad-LPs). Die Sitemap iteriert
 * `TRANSLATED_KEYS` und würde sie sonst mit hreflang-Alternates listen – Widerspruch
 * zu noindex. Banner/Routing nutzen `localizedSlugs` weiterhin.
 */
export const NOINDEX_KEYS = new Set<TranslatedKey>(['concierge', 'wg']);

/**
 * Nur-DE-Pfade ohne EN/FR-Entsprechung (eigener Markt-Content / DE-only by
 * design): Tool-Seite, Blog (DE-Mietrecht), Vergleiche (DE-Tools), Legal.
 *
 * AUSNAHME `/legal/widerruf`: steht seit dem 03.08.2026 in `localizedSlugs`
 * (`legalWiderruf`) und darf deshalb hier NICHT mehr stehen — sonst gäbe die
 * Sitemap die deutsche URL zweimal aus, einmal mit und einmal ohne
 * hreflang-Alternates, und der Orphan-Guard prüfte dasselbe Ziel doppelt.
 * Einzige Wahrheit für die DE-only-Sitemap-Menge – von `sitemap.xml/route.ts`
 * (Sitemap-Ausgabe) UND `no-orphan-pages.test.ts` (Orphan-Guard) importiert,
 * damit beide exakt dieselben URLs prüfen (keine zweite Wahrheit).
 */
export const DE_ONLY_PATHS = [
  // Use-Case-Index (Lauf 1 DE-only by design – Spec funktionen-use-case-index;
  // EN/FR als getriggertes Follow-up nach Stabilisierung).
  '/funktionen',
  '/mieterselbstauskunft',
  '/blog',
  '/blog/anti-profiling-was-bedeutet-das',
  '/blog/bewerber-vorauswahl-rechtssicher',
  '/blog/dsgvo-auskunftsrecht-vermieter',
  '/blog/dsgvo-vermietung',
  '/blog/erstvermietung-checkliste',
  '/blog/mieterselbstauskunft-vorlage',
  '/blog/pauschalpreis-vs-maklerprovision',
  '/blog/schufa-bewerber-pruefung-rechtlich',
  '/blog/whatsapp-vermietung',
  '/vergleich-everreal',
  '/vergleich-immocloud',
  '/vergleich-objego',
  '/vergleich-ohne-makler',
  '/vergleich-vermietet-de',
  '/legal',
  '/legal/impressum',
  '/legal/datenschutz',
  '/legal/cookie-richtlinie',
  // '/legal/widerruf' → siehe localizedSlugs.legalWiderruf (übersetzt, mit hreflang)
  '/legal/kuendigung',
  '/legal/dsa-meldung',
  '/legal/sub-prozessoren',
  '/legal/avv',
  '/legal/agb-b2b',
  '/legal/agb-b2c',
  '/legal/nutzungsbedingungen',
  '/legal/responsible-disclosure',
  '/legal/barrierefreiheit',
] as const;

/** Slug einer übersetzten Seite in der gewünschten Sprache. */
export function route(key: TranslatedKey, locale: Locale): string {
  return localizedSlugs[key][locale];
}

/** Locale aus dem Pfad ableiten (Marketing: /en* → en, /fr* → fr, sonst de). */
export function localeFromPath(pathname: string): Locale {
  if (pathname === '/en' || pathname.startsWith('/en/')) return 'en';
  if (pathname === '/fr' || pathname.startsWith('/fr/')) return 'fr';
  return 'de';
}

/**
 * Übersetzten RouteKey aus dem aktuellen Pfad ermitteln (für den Sprachumschalter).
 * Liefert null für nicht-übersetzte Seiten (Blog/Legal/…) → Umschalter bleibt disabled.
 */
export function pathToTranslatedKey(pathname: string): TranslatedKey | null {
  for (const key of TRANSLATED_KEYS) {
    const s = localizedSlugs[key];
    if (pathname === s.de || pathname === s.en || pathname === s.fr) {
      return key;
    }
  }
  return null;
}

/** Header nav – page-key → label, matches chrome.js NAV (mapped to DE slugs). */
export const headerNav = [
  { href: marketingRoutes.service, label: 'Wie es funktioniert' },
  { href: marketingRoutes.warumLocari, label: 'Warum Locari' },
  { href: marketingRoutes.antiProfiling, label: 'Anti-Profiling' },
  { href: marketingRoutes.preise, label: 'Preise' },
  { href: marketingRoutes.faq, label: 'FAQ' },
] as const;
